import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { MonitorPlay, ArrowRight } from "lucide-react";

export default function Pair() {
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase().slice(0, 6));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  // Auto-submit if a complete code arrived via URL parameter
  useEffect(() => {
    const c = (params.get("code") || "").toUpperCase().slice(0, 6);
    if (c.length === 6) {
      // small delay so user can see the code before redirect
      const t = setTimeout(() => doSubmit(c), 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSubmit = async (rawCode) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/player/pair", { pairing_code: rawCode.trim().toUpperCase() });
      localStorage.setItem("signage_device_id", data.device_id);
      localStorage.setItem("signage_device_name", data.name);
      nav("/player");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await doSubmit(code);
  };

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-8" data-testid="pair-page">
      <div className="flex items-center gap-3 mb-12">
        <div className="h-12 w-12 bg-[#002FA7] flex items-center justify-center">
          <MonitorPlay className="h-7 w-7" />
        </div>
        <span className="font-display font-bold tracking-tight text-2xl">DigiSign</span>
      </div>
      <p className="text-xs uppercase tracking-[0.3em] text-zinc-400">Pair this display</p>
      <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tight mt-4 mb-10 text-center">
        Enter pairing code
      </h1>
      <form onSubmit={submit} className="w-full max-w-md flex flex-col items-center gap-4">
        <input
          data-testid="pair-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={6}
          placeholder="XXXXXX"
          className="w-full bg-transparent border-b-2 border-white/30 focus:border-white outline-none text-center font-mono-disp text-6xl tracking-[0.4em] py-4"
        />
        <button
          type="submit"
          data-testid="pair-submit-button"
          disabled={busy || code.length < 4}
          className="bg-white text-black px-8 py-3 font-medium hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2"
        >
          {busy ? "Pairing…" : (<>Connect display <ArrowRight className="h-4 w-4" /></>)}
        </button>
        {err && <p className="text-red-400 text-sm" data-testid="pair-error">{err}</p>}
      </form>
      <p className="text-xs text-zinc-500 mt-16">
        Get a pairing code from the admin console → Devices.
      </p>
    </div>
  );
}
